/**
 * The Crisis Desk — the screen. Readiness from the records, the playbooks
 * with their gaps and their priced drill, the drill to run first, and the
 * log of exercises run.
 */
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useSession } from "../../app/SessionContext";
import type { ClosureCost } from "../../domain/capacity";
import type { Closure } from "../../domain/closures";
import {
  answerCrisis,
  buildCrisisDesk,
  CRISIS_MODEL_PROMPT,
  materialForCrisis,
  PLAYBOOKS,
  type CrisisDesk,
  type CrisisEntered,
  type PlaybookState,
} from "../../domain/crisis";
import {
  answerContinuity,
  buildContinuityDesk,
  CONTINUITY_MODEL_PROMPT,
  materialForContinuity,
  type ContinuityDesk,
} from "../../domain/continuity";
import { defaultRateCard } from "../../domain/decision";
import { briefWindowFor } from "../../domain/dbrief";
import type { DocumentRow } from "../../domain/library";
import { Button, Card, ErrorState, Field, Input, Select, Skeleton } from "../../design/components";
import { Page } from "../../layout/AppShell";
import * as store from "../decision/store";
import { useIntelligence } from "../intelligence/gather";
import { AskCard, Refs, Tiles, x as s } from "./shared";
import { ContinuityView } from "./ContinuityView";
import { useFinance } from "./FinanceForm";
import { useEntered } from "./store";
import { useScreenContext } from "../../assistant/screen";
import { t } from "../../i18n";

function useCrisisRecords(enabled: boolean, zones: string[]) {
  const session = useSession();
  const source = session.source;
  const siteId = session.site.id;
  return useQuery({
    queryKey: ["crisis-records", siteId, zones.join(",")],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const now = new Date();
      const quiet = async <T,>(p: Promise<T>, fb: T) => p.catch(() => fb);
      const to = new Date(now.getTime() + 90 * 86_400_000).toISOString();
      const [documents, kpis, critical, closures, rateCard] = await Promise.all([
        quiet(source.listDocuments({ siteId }), [] as DocumentRow[]),
        quiet(source.listKpis(siteId), []),
        quiet(source.criticalPositions(siteId, now.toISOString(), to), null),
        quiet(source.listClosures(siteId, new Date(now.getTime() - 180 * 86_400_000).toISOString()), [] as Closure[]),
        store.loadRateCard(source, siteId),
      ]);
      const closureCosts: Record<string, ClosureCost | null> = {};
      await Promise.all(
        zones.map(async (zone) => {
          const w = briefWindowFor(zone, closures, now);
          closureCosts[zone] = await quiet(
            source.closureCost({ siteId, positionCode: zone, from: w.from, to: w.to }),
            null,
          );
        }),
      );
      return { documents, kpis, critical, closures, closureCosts, rateCard: rateCard ?? defaultRateCard() };
    },
  });
}

export function CrisisPage() {
  const session = useSession();
  const gathered = useIntelligence();
  const zones = useMemo(
    () => [
      ...new Set(
        (gathered.data?.report.findings ?? [])
          .map((f) => f.where?.match(/\b(RWY-[\w-]+)\b/)?.[1])
          .filter((z): z is string => !!z),
      ),
    ],
    [gathered.data],
  );
  const records = useCrisisRecords(!!gathered.data, zones);
  const entered = useEntered<CrisisEntered>("executive.crisis", {});
  const desk = useMemo<CrisisDesk | null>(() => {
    if (!gathered.data || !records.data) return null;
    return buildCrisisDesk({
      now: new Date(),
      siteCode: session.site.code,
      alerts: gathered.data.inputs.alerts,
      compliance: gathered.data.inputs.compliance,
      documents: records.data.documents,
      kpis: records.data.kpis,
      critical: records.data.critical,
      closureCosts: records.data.closureCosts,
      closures: records.data.closures,
      rateCard: records.data.rateCard,
      entered: entered.value,
    });
  }, [gathered.data, records.data, entered.value, session.site.code]);
  const finance = useFinance();
  /*
   * The prolonged desk, built from the same records plus the finance.
   *
   * Separate from `buildCrisisDesk` because the two answer different
   * questions and share almost nothing: readiness for the first quarter of an
   * hour is about records and rehearsal, and surviving a three-month closure
   * is about cash. Folding them into one builder would have made the crisis
   * desk depend on finance it does not need.
   */
  const continuity = useMemo<ContinuityDesk | null>(() => {
    if (!records.data) return null;
    return buildContinuityDesk({
      now: new Date(),
      siteCode: session.site.code,
      finance: finance.value,
      documents: records.data.documents,
      compliance: gathered.data?.inputs.compliance ?? null,
    });
  }, [records.data, gathered.data, finance.value, session.site.code]);
  const [open, setOpen] = useState<string | null>(null);
  const shown = desk ? (desk.playbooks.find((p) => p.playbook.key === open) ?? desk.drillFirst) : null;
  useScreenContext(
    desk
      ? {
          route: "/crisis",
          title: "Crisis Desk",
          summary: desk.boardLine,
          /* Both desks' material, so a question about a three-month closure is
             answered from the postures and the runway dates rather than from
             the first-quarter-hour playbooks that happen to share the screen. */
          material: continuity
            ? `${materialForCrisis(desk)}\n\n${materialForContinuity(continuity)}`
            : materialForCrisis(desk),
          figures: continuity ? new Set([...desk.figures, ...continuity.figures]) : desk.figures,
          own: (q) => answerCrisis(q, desk) ?? (continuity ? answerContinuity(q, continuity) : null),
          /* Both prompts. The continuity one carries a rule the crisis one does
             not — that a model must never state or imply the aerodrome is fit
             to reopen — and dropping it on a screen that now discusses
             reopening would remove the guard exactly where it is needed. */
          prompt: continuity ? `${CRISIS_MODEL_PROMPT} ${CONTINUITY_MODEL_PROMPT}` : CRISIS_MODEL_PROMPT,
          offers: [
            { label: "Which drill first?", ask: "which drill should we run first?" },
            { label: "What is missing?", ask: "what readiness gaps are there?" },
            { label: "The board line", ask: "give me the board line" },
            { label: "How long does the cash last?", ask: "how long does the cash last if we close?" },
            {
              label: "Airspace shut for months",
              ask: "what happens if airspace is closed for more than three months?",
            },
          ],
        }
      : null,
  );
  return (
    <Page
      eyebrow={t("{site} · Executive", { site: session.site.code })}
      title="Crisis Desk"
      actions={
        <Button variant="primary" onClick={() => window.print()}>
          {t("Print")}
        </Button>
      }
    >
      <p className={s.lead}>
        {t(
          "How ready the airport is for an emergency, and what happens in the first 15 minutes.",
        )}
      </p>
      {(gathered.isPending || records.isPending) && <Skeleton rows={8} />}
      {gathered.isError && <ErrorState error={gathered.error} retry={() => void gathered.refetch()} />}
      {desk && (
        <div className={s.page}>
          <Tiles items={desk.tiles} currency={desk.currency} />
          {entered.savedTo && <p className={s.saved}>{entered.savedTo}</p>}
          <section className={s.cards} aria-label={t("Playbooks")}>
            {desk.playbooks.map((p) => (
              <button
                key={p.playbook.key}
                type="button"
                className={`${s.card} ${s.select} ${shown?.playbook.key === p.playbook.key ? s.cardOpen : ""}`}
                onClick={() => setOpen(p.playbook.key)}
                aria-pressed={shown?.playbook.key === p.playbook.key}
                data-testid={`playbook-${p.playbook.key}`}
              >
                <div className={s.cardHead}>
                  <span className={p.score === 100 ? s.tagGreen : p.score >= 50 ? s.tag : s.tagRed}>
                    {t("{score}% ready", { score: p.score })}
                  </span>
                  {desk.drillFirst?.playbook.key === p.playbook.key && (
                    <span className={s.tagBlue}>{t("drill first")}</span>
                  )}
                </div>
                <h3 className={s.cardTitle}>{t(p.playbook.name)}</h3>
                <p className={s.cardText}>{t(p.playbook.trigger)}</p>
                <p className={s.cardText}>
                  {p.readiness.some((r) => !r.met)
                    ? t("{met} of {total} needs met: missing {missing}", {
                        met: p.met,
                        total: p.readiness.length,
                        missing: p.readiness
                          .filter((r) => !r.met)
                          .map((r) => t(r.need.what))
                          .join("; "),
                      })
                    : t("{met} of {total} needs met", { met: p.met, total: p.readiness.length })}
                </p>
              </button>
            ))}
          </section>
          {shown && <PlaybookView p={shown} desk={desk} />}
          {continuity && <ContinuityView desk={continuity} />}
          <AskCard
            title="The line for the board"
            sub="Summary for the board, from the airport's own figures"
            /* Both desks, as the screen context above. The board reads one
               screen and will ask about either half of it; an ask box that
               answers only the playbooks would refuse the questions the
               prolonged section exists to raise. */
            board={continuity ? [...desk.boardLine, ...continuity.boardLine] : desk.boardLine}
            material={
              continuity
                ? `${materialForCrisis(desk)}\n\n${materialForContinuity(continuity)}`
                : materialForCrisis(desk)
            }
            figures={continuity ? new Set([...desk.figures, ...continuity.figures]) : desk.figures}
            own={(q) => answerCrisis(q, desk) ?? (continuity ? answerContinuity(q, continuity) : null)}
            prompt={continuity ? `${CRISIS_MODEL_PROMPT} ${CONTINUITY_MODEL_PROMPT}` : CRISIS_MODEL_PROMPT}
            placeholder={t(
              "Which drill first? How long does the cash last if we close? What happens if airspace shuts for three months?",
            )}
          />
          <DrillLog entered={entered.value} onSave={(e) => void entered.save(e)} />
          {desk.gaps.length > 0 && (
            <Card
              title="What the platform could not supply"
              sub="What this screen doesn't cover yet"
            >
              <ul className={s.plain}>
                {desk.gaps.map((g) => (
                  <li key={g}>{g}</li>
                ))}
              </ul>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}

function PlaybookView({ p, desk }: { p: PlaybookState; desk: CrisisDesk }) {
  return (
    <Card
      title={p.playbook.name}
      sub={t("{met} of {total} needs met · {note}", { met: p.met, total: p.readiness.length, note: p.drillNote })}
      className={undefined}
    >
      <div className={s.two}>
        <div>
          <div className={s.colTitle}>{t("Who holds the first quarter of an hour")}</div>
          <p className={s.cardText}>{p.playbook.holds.map((h) => t(h)).join(" · ")}</p>
          <div className={s.colTitle}>{t("The first quarter of an hour")}</div>
          <ol className={s.plain}>
            {p.playbook.firstQuarterHour.map((a) => (
              <li key={a}>{t(a)}</li>
            ))}
          </ol>
          <div className={s.colTitle}>{t("The drill, priced")}</div>
          <dl className={s.kv}>
            {p.drill.map((d) => (
              <div key={d.label}>
                <dt>{d.label}</dt>
                <dd>
                  {d.value}
                  {d.assumed && <span className={s.tag}> {t("rate card")}</span>}
                  <Refs list={d.sources} />
                </dd>
              </div>
            ))}
            {p.drill.length === 0 && <p className={s.muted}>{t("nothing on the platform prices this scenario")}</p>}
          </dl>
        </div>
        <div>
          <div className={s.colTitle}>{t("What the platform must show, and does")}</div>
          <div className={s.tableWrap}>
            <table className={s.table}>
              <tbody>
                {p.readiness.map((r) => (
                  <tr key={r.need.key} data-testid={`need-${p.playbook.key}-${r.need.key}`}>
                    <td>
                      <span className={r.met ? s.tagGreen : s.tagRed}>{r.met ? t("met") : t("gap")}</span>
                    </td>
                    <td>
                      <b>{t(r.need.what)}</b>
                      <div className={s.muted}>{r.basis}</div>
                      {r.todo && <div className={s.muted}>{t("To close it: {todo}", { todo: r.todo })}</div>}
                      <Refs list={r.sources} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className={s.muted}>
            {t("{currency} figures on the rate card are starting values until the airport edits them.", {
              currency: desk.currency,
            })}
          </p>
        </div>
      </div>
    </Card>
  );
}

function nameOf(key: string): string {
  const p = PLAYBOOKS.find((x) => x.key === key);
  return p ? t(p.name) : key;
}

function DrillLog({ entered, onSave }: { entered: CrisisEntered; onSave: (e: CrisisEntered) => void }) {
  const [playbook, setPlaybook] = useState(PLAYBOOKS[0].key);
  const [at, setAt] = useState(new Date().toISOString().slice(0, 10));
  const [lesson, setLesson] = useState("");
  const rows = [...(entered.exercises ?? [])].sort((a, b) => b.at.localeCompare(a.at));
  return (
    <Card
      title="Exercises and drills"
      sub="Drills held and lessons learned"
    >
      <form
        className={s.form}
        onSubmit={(e) => {
          e.preventDefault();
          if (!lesson.trim()) return;
          onSave({ ...entered, exercises: [...(entered.exercises ?? []), { at, playbook, lesson: lesson.trim() }] });
          setLesson("");
        }}
      >
        <Field label="playbook">
          <Select value={playbook} onChange={(e) => setPlaybook(e.target.value)} aria-label={t("drill playbook")}>
            {PLAYBOOKS.map((p) => (
              <option key={p.key} value={p.key}>
                {t(p.name)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="date">
          <Input type="date" value={at} onChange={(e) => setAt(e.target.value)} aria-label={t("drill date")} />
        </Field>
        <Field label="what was learned">
          <Input
            value={lesson}
            onChange={(e) => setLesson(e.target.value)}
            placeholder={t("the one thing to fix before the real one")}
            aria-label={t("drill lesson")}
          />
        </Field>
        <Button type="submit" disabled={!lesson.trim()}>
          {t("Record the drill")}
        </Button>
      </form>
      {rows.length > 0 && (
        <ul className={s.plain} style={{ marginTop: 10 }}>
          {rows.map((r, i) => (
            <li key={i} data-testid="drill-row">
              <b>{r.at}</b> — {nameOf(r.playbook)}: {r.lesson}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
